// UPLOAD THE DELIVERABLES TO PROCORE / ACC, beside "Resend deliverables"
// (owner, 2026-11-04: "add a button next to resend that has upload to procore,
// like the shopdrawings button, only for owners/admin/company admin. the
// button will upload the deliverables to the procore shopdrawings path").
//
// THE SAME POPUP AS THE SHOP DRAWINGS BUTTON, and literally so: `boxesOf` is
// imported from it rather than copied, because the rule it encodes - one box
// per destination when the server lists them, else one per platform, each
// shown UNTICKED AND DISABLED WITH ITS REASON rather than hidden - is a
// decision the owner made once, and a second copy of it would answer
// differently the first time either side changed.
//
// WHAT THIS IS NOT: a per-recipient button. Resend is per recipient because a
// mail goes to a person; this uploads a file to a folder, which happens once
// for the issue however many people are on the list. So it sits ONCE at the
// top of the recipients block, next to the Resend buttons rather than in front
// of each name.
import React, { useState } from "react";
import { Button, Checkbox, Modal, Spin, Tooltip, message } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import { boxesOf } from "pages/ShopDrawings/SendExternalButton";
import { getIssueDeliverableTargets, uploadIssueDeliverables } from "api/issue/issueApi";

const NAME = { procore: "Procore", acc: "ACC" };

/**
 * The message after an upload: what was queued, and every reason something was
 * not - each reason said once, with how many files it covers.
 *
 * Its own copy rather than the shop drawings one, because every noun differs
 * (files, not drawings; the issue's deliverables, not a revision) and the shop
 * drawings wording is asserted by that page's test.
 */
export function uploadMessage(r) {
  const results = (r && r.results) || [];
  const queued = results.filter((x) => x.queued);
  const per = ["procore", "acc"]
    .map((p) => {
      const n = queued.filter((x) => x.provider === p).length;
      return n ? `${n} file${n === 1 ? "" : "s"} to ${NAME[p]}` : "";
    })
    .filter(Boolean);
  const reasons = new Map();
  for (const x of results) {
    if (!x.skipped) continue;
    const k = `${NAME[x.provider] || x.provider || "Not sent"}: ${x.skipped}`;
    reasons.set(k, (reasons.get(k) || 0) + (x.count || 1));
  }
  const notSent = [...reasons.entries()].map(([k, n]) => `${k}${n > 1 ? ` (${n})` : ""}`);
  if (!queued.length) return { level: "warning", text: `Nothing uploaded. ${notSent.join(". ")}` };
  const text = `Uploading ${per.join(" and ")} into the shop drawings folder.`;
  return notSent.length ? { level: "warning", text: `${text} Not uploaded: ${notSent.join(". ")}` } : { level: "success", text };
}

export default function DeliverableUploadButton({ issueId, hasDeliverables, onSent }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState([]);

  const openIt = async () => {
    setOpen(true);
    setTargets(null);
    setLoading(true);
    try {
      const t = await getIssueDeliverableTargets(issueId);
      setTargets(t);
      // Everything that can take them starts ticked - picking one is unticking
      // the other, as on the shop drawings popup.
      setChosen(boxesOf(t).filter((b) => b.ready).map((b) => b.id));
    } catch (e) {
      message.error(e.message || "Could not read the Procore / ACC settings");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const go = async () => {
    setBusy(true);
    try {
      const many = !!(targets && Array.isArray(targets.destinations) && targets.destinations.length);
      const r = many ? await uploadIssueDeliverables(issueId, [], chosen) : await uploadIssueDeliverables(issueId, chosen);
      const m = uploadMessage(r);
      message[m.level](m.text, m.level === "warning" ? 10 : 5);
      setOpen(false);
      if (onSent) onSent();
    } catch (e) {
      message.error(e.message || "Could not upload to Procore / ACC");
    } finally {
      setBusy(false);
    }
  };

  const count = targets && typeof targets.files === "number" ? targets.files : 0;
  return (
    <>
      <Tooltip title={hasDeliverables
        ? "Upload this issue's deliverables to Procore / ACC - into the project's shop drawings folder"
        : "Nothing has been delivered on this issue yet"}
      >
        <Button
          size="small"
          icon={<CloudUploadOutlined />}
          disabled={!hasDeliverables}
          onClick={openIt}
          data-testid="issue-deliverable-upload"
        >
          Upload to Procore / ACC
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title="Upload the deliverables to Procore / ACC"
        okText="Upload"
        okButtonProps={{ loading: busy, disabled: !chosen.length, "data-testid": "issue-deliverable-upload-go" }}
        onOk={go}
        onCancel={() => { if (!busy) setOpen(false); }}
        destroyOnClose
      >
        {loading || !targets ? <Spin /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {boxesOf(targets).map((t) => (
              <div key={t.id}>
                <Checkbox
                  checked={chosen.includes(t.id)}
                  disabled={!t.ready}
                  onChange={(e) => setChosen((c) => (e.target.checked ? [...new Set([...c, t.id])] : c.filter((x) => x !== t.id)))}
                  data-testid={`issue-deliverable-${t.id}`}
                >
                  <b>{t.label}</b>
                </Checkbox>
                <div style={{ color: t.ready ? "#667085" : "#b42318", fontSize: 12, marginLeft: 24 }} data-testid={`issue-deliverable-${t.id}-note`}>
                  {t.ready ? `Into ${t.path || "the shop drawings folder"}` : `Cannot upload: ${t.reason || "not set up"}`}
                </div>
              </div>
            ))}
            <div style={{ color: "#667085", fontSize: 12 }}>
              {count
                ? `${count} deliverable file${count === 1 ? "" : "s"} - the same files the deliverables email carries - uploaded under their own names, so a sheet delivered again later lands as the next version of the same file.`
                : "Nothing has been delivered on this issue yet."}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
